import { useInView } from "../lib/useInView.js";
import { ScoreChart } from "../components/ScoreChart.js";
import { toneColor, toneSoft } from "../data/game.js";
import { PLAN_LIMITS, siteStats } from "../store/types.js";
import type { Plan, SavedSite } from "../store/types.js";

interface Props {
  site: SavedSite;
  plan: Plan;
  onReplay: () => void;
  onUpgrade: () => void;
  onShare: () => void;
}

export function SiteHistory({ site, plan, onReplay, onUpgrade, onShare }: Props) {
  const [ref, seen] = useInView<HTMLDivElement>(0.2);
  const st = siteStats(site);
  const color = toneColor(site.tone);
  const deep = PLAN_LIMITS[plan].deepHistory;
  const unlimited = PLAN_LIMITS[plan].unlimited;
  const lensEntries = Object.entries(site.lenses);

  return (
    <section className="history" ref={ref}>
      <div className="gcard history-card">
        <div className="history-head">
          <div className="history-title-row">
            <span className="history-fav" style={{ background: toneSoft(site.tone) }}>{site.name[0]}</span>
            <div>
              <h3 className="gdisplay history-url">{site.url}</h3>
              <p className="glabel history-sub">
                {st.plays} {st.plays === 1 ? "play" : "plays"} · tracking since {site.runs[0]?.date ?? "now"}
              </p>
            </div>
          </div>
          <div className="history-actions">
            <button className="gbtn" onClick={onReplay}>↻ Re-play{unlimited ? "" : " (1 coin)"}</button>
            <button className="gbtn gbtn--yellow" onClick={onShare}>Share</button>
          </div>
        </div>

        <div className="history-grid">
          <div className="history-chart">
            <div className="history-chart-head">
              <div>
                <span className="glabel">Score over time</span>
                <div className="history-current">
                  <span className="gdisplay history-current-num" style={{ color }}>
                    {st.current}<small>/100</small>
                  </span>
                  <span className={`delta ${st.total > 0 ? "is-up" : st.total < 0 ? "is-down" : "is-flat"}`}>
                    {st.total > 0 ? "▲" : st.total < 0 ? "▼" : "■"} {st.total > 0 ? "+" : ""}{st.total} all time
                  </span>
                </div>
              </div>
            </div>
            <ScoreChart runs={site.runs} color={color} run={seen} />
          </div>

          <div className="runlog">
            <span className="glabel runlog-title">Play history</span>
            {[...site.runs].reverse().map((r, i, arr) => {
              const realIdx = site.runs.length - 1 - i;
              const prev = realIdx > 0 ? site.runs[realIdx - 1].score : r.score;
              const d = r.score - prev;
              const latest = i === 0;
              return (
                <div key={realIdx} className="runlog-item">
                  <div className="runlog-rail">
                    <span className={`runlog-dot ${latest ? "is-latest" : ""}`} />
                    {i < arr.length - 1 && <span className="runlog-stem" />}
                  </div>
                  <div className="runlog-body">
                    <div className="runlog-meta">
                      <span className="runlog-date">{r.date}</span>
                      <span className="runlog-score" style={{ color: latest ? color : "var(--ink)" }}>{r.score}</span>
                      {realIdx > 0 && (
                        <span className="runlog-delta" style={{ color: d >= 0 ? "var(--green)" : "var(--red)" }}>
                          {d >= 0 ? "▲ +" : "▼ "}{d}
                        </span>
                      )}
                      {latest && <span className="pill pill--green" style={{ fontSize: 9, padding: "3px 8px" }}>Latest</span>}
                    </div>
                    <p className="runlog-note">{r.note}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lenses">
          <div className="lenses-head">
            <div>
              <span className="glabel" style={{ color: "var(--blue)" }}>Lens by lens (deep view)</span>
            </div>
            {deep ? <span className="pill pill--blue">Pro · unlocked</span> : <span className="pill">🔒 Pro feature</span>}
          </div>

          <div style={{ position: "relative" }}>
            <div className={`lenses-grid ${deep ? "" : "locked-blur"}`}>
              {lensEntries.map(([name, val]) => (
                <div key={name} className="lens">
                  <div className="lens-top">
                    <span className="lens-name">{name}</span>
                    <span
                      className="lens-val"
                      style={{ color: val < 45 ? "var(--red)" : val < 65 ? "var(--ink)" : "var(--green)" }}
                    >
                      {val}
                    </span>
                  </div>
                  <div className="lens-bar">
                    <i
                      style={{
                        width: `${val}%`,
                        background: val < 45 ? "var(--red)" : val < 65 ? "var(--yellow)" : "var(--green)"
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {!deep && (
              <div className="lock-overlay">
                <div className="lock-overlay-card">
                  <span className="slot-lock-ic">🔒</span>
                  <h4 className="gdisplay">See every lens move, run by run.</h4>
                  <p>Pro tracks all six lenses over time, flags the ones sliding backward, and tells you which fix moved which number.</p>
                  <button className="gbtn gbtn--primary" onClick={onUpgrade}>Unlock with Pro</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
